import clearDynamicForm from "@iewcasia/Store/Handlers/DynamicForm/ClearForm";
import { setGA4Event } from "@iewcasia/Store/Handlers/Utilities/CustomTracking";
import { CustomState } from "@iewcasia/Store/Reducers";
import { submitDynamicForm } from "@iewcasia/Services/DynamicFormService";
import {
    clearDynamicFormUtm,
    ensureDynamicFormUtmSnapshot,
    getDynamicFormUtm,
} from "@iewcasia/Utilities/dynamicFormUtmStorage";
import { buildDynamicFormSubmitPayload } from "@iewcasia/Utilities/buildDynamicFormSubmitPayload";
import { applyDynamicFormUtmToFields } from "@iewcasia/Utilities/dynamicFormUtmMapping";
import { validateFormWithScroll } from "@iewcasia/Utilities/formValidationScroll";
import { colorStrings } from "@iewcasia/Utilities/variables";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import resetReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ResetReCaptcha";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React, {
    createContext,
    createRef,
    RefObject,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/**
 * IEIXOP-6695 Dynamic Form (Salesforce) — container widget.
 * Drop field widgets from "SalesForce Dynamic" into the Content zone.
 * Field Key on each widget is the Salesforce API name.
 * Form Id is sent to Salesforce as LeadSource.
 */
const enum fields {
    formId = "formId",
    submitButtonText = "submitButtonText",
    showCancelButton = "showCancelButton",
    cancelButtonText = "cancelButtonText",
    successMessage = "successMessage",
    enableRecaptcha = "enableRecaptcha",
    enableUtm = "enableUtm",
    utmSourceMediumField = "utmSourceMediumField",
    utmCampaignField = "utmCampaignField",
    utmChannelGroupField = "utmChannelGroupField",
    enableGa4 = "enableGa4",
    ga4EventName = "ga4EventName",
    ga4FormType = "ga4FormType",
    ga4FormId = "ga4FormId",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.formId]: string;
        [fields.submitButtonText]: string;
        [fields.showCancelButton]: boolean;
        [fields.cancelButtonText]: string;
        [fields.successMessage]: string;
        [fields.enableRecaptcha]: boolean;
        [fields.enableUtm]: boolean;
        [fields.utmSourceMediumField]: string;
        [fields.utmCampaignField]: string;
        [fields.utmChannelGroupField]: string;
        [fields.enableGa4]: boolean;
        [fields.ga4EventName]: string;
        [fields.ga4FormType]: string;
        [fields.ga4FormId]: string;
    };
}

type Validator = () => boolean;

export type DynamicFormAttachmentSlot = {
    fileKey: string;
    nameKey: string;
};

type ContextType = {
    validators: {
        [key: string]: Validator | undefined;
    };
    registerFieldKeys: (widgetId: string, fieldKeys: string[]) => void;
    registerAttachmentSlots: (widgetId: string, slots: DynamicFormAttachmentSlot[]) => void;
    unregisterFieldKeys: (widgetId: string) => void;
    unregisterAttachmentSlots: (widgetId: string) => void;
};

export const DynamicFormContext = createContext<ContextType>({
    validators: {},
    registerFieldKeys: () => undefined,
    registerAttachmentSlots: () => undefined,
    unregisterFieldKeys: () => undefined,
    unregisterAttachmentSlots: () => undefined,
});

export interface DynamicFormStyles {
    container?: GridContainerProps;
    formGridItem?: GridItemProps;
    zoneWrapper?: InjectableCss;
    reCaptchaWrapper?: InjectableCss;
    submitError?: TypographyPresentationProps;
    actionsWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
    successModal?: ModalPresentationProps;
    modalContainer?: GridContainerProps;
    messageTextGridItem?: GridItemProps;
    messageText?: TypographyPresentationProps;
    buttonGridItem?: GridItemProps;
    continueButton?: ButtonPresentationProps;
    messageErrorTextGridItem?: GridItemProps;
    messageErrorText?: TypographyPresentationProps;
}

export const dynamicFormStyles: DynamicFormStyles = {
    container: {
        // Match Contact Us ContactForm GridContainer default inset (~15px via gap/2).
        gap: 30,
        css: css`
            width: 100%;

            /* Form id is "dynamicForm" (shared with validateFormWithScroll / GA4 defaults). */
            /* stylelint-disable selector-id-pattern */
            #dynamicForm {
                width: 100%;
                display: block;
                box-sizing: border-box;
            }

            #dynamicForm textarea {
                min-height: 120px;
                resize: vertical;
            }
            /* stylelint-enable selector-id-pattern */
        `,
    },
    formGridItem: {
        width: 12,
        css: css`
            width: 100%;
            max-width: 100%;
            flex-basis: 100%;
        `,
    },
    zoneWrapper: {
        css: css`
            width: 100%;
            display: block;
        `,
    },
    reCaptchaWrapper: {
        css: css`
            margin-top: 20px;
        `,
    },
    submitError: {
        css: css`
            margin-top: 12px;
        `,
    },
    /* Match Contact Us: left-aligned actions with 20px under reCAPTCHA. */
    actionsWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            gap: 12px;
            justify-content: flex-start;
            align-items: center;
            width: 100%;
            margin-top: 20px;
        `,
    },
    submitButton: {
        css: css`
            float: none;
        `,
    },
    successModal: { sizeVariant: "small" },
    messageTextGridItem: { width: 12 },
    buttonGridItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
        `,
    },
    messageErrorTextGridItem: {
        width: 12,
        css: css`
            color: ${getColor(colorStrings.primaryMain)};
        `,
    },
    messageErrorText: {
        css: css`
            line-height: 25px !important;
        `,
    },
};

const styles = dynamicFormStyles;

const recaptchaLocation = "ContactUs";

/** Maps Dynamic Form Redux state into widget props. */
const mapStateToProps = (state: CustomState) => ({
    fieldValues: state.customDynamicForm?.fieldValues ?? {},
    isAuthenticated: state.context?.session?.isAuthenticated && !state.context?.session?.isGuest,
    billTo: getBillToState(state, state.context?.session?.billToId).value,
});

const mapDispatchToProps = {
    clearDynamicForm,
    resetReCaptcha,
    validateReCaptcha: makeHandlerChainAwaitable<
        { location: string; recaptchaRef: RefObject<ReCAPTCHA | null> },
        boolean
    >(validateReCaptcha),
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const widgetGroup = "SalesForce Dynamic" as any;

/** Scrolls the page so the reCAPTCHA widget is visible. */
const scrollToRecaptcha = (recaptchaRef: RefObject<ReCAPTCHA | null>) => {
    const recaptchaElement = recaptchaRef.current?.props.id
        ? document.getElementById(recaptchaRef.current.props.id)
        : document.querySelector("[data-sitekey]")?.closest('[class*="wrapper"]');

    if (recaptchaElement) {
        const elementTop = recaptchaElement.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({
            top: elementTop - 120,
            behavior: "smooth",
        });
    }
};

type DynamicFormActionsProps = {
    submitting: boolean;
    showCancelButton: boolean;
    cancelButtonText: string;
    submitButtonText: string;
    onCancel: () => void;
};

/** Cancel and Submit buttons for the Dynamic Form. */
const DynamicFormActions: React.FC<DynamicFormActionsProps> = ({
    submitting,
    showCancelButton,
    cancelButtonText,
    submitButtonText,
    onCancel,
}) => (
    <StyledWrapper {...styles.actionsWrapper}>
        {showCancelButton ? (
            <Button
                {...styles.cancelButton}
                variant="secondary"
                type="button"
                onClick={onCancel}
                disabled={submitting}
            >
                {cancelButtonText}
            </Button>
        ) : null}
        <Button {...styles.submitButton} type="submit" disabled={submitting}>
            {submitButtonText}
        </Button>
    </StyledWrapper>
);

type DynamicFormSuccessModalProps = {
    isOpen: boolean;
    successMessage: string;
    onClose: () => void;
};

/** Strips HTML tags for modal accessibility labels. */
const toPlainText = (html: string) => html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

/** Success modal shown after Salesforce Lead insert succeeds. */
const DynamicFormSuccessModal: React.FC<DynamicFormSuccessModalProps> = ({
    isOpen,
    successMessage,
    onClose,
}) => (
    <Modal
        headline={translate("Message Sent")}
        contentLabel={toPlainText(successMessage)}
        {...styles.successModal}
        isOpen={isOpen}
        handleClose={onClose}
    >
        <GridContainer {...styles.modalContainer}>
            <GridItem {...styles.messageTextGridItem}>
                <Typography {...styles.messageText}>{parse(successMessage, parserOptions)}</Typography>
            </GridItem>
            <GridItem {...styles.buttonGridItem}>
                <Button {...styles.continueButton} onClick={onClose}>
                    {translate("Continue")}
                </Button>
            </GridItem>
        </GridContainer>
    </Modal>
);

type DynamicFormErrorModalProps = {
    isOpen: boolean;
    submitError: string;
    onClose: () => void;
};

/** Renders a multi-line submit error as separate lines. */
const DynamicFormErrorLines: React.FC<{ message: string }> = ({ message }) => (
    <>
        {message.split("\n").map((line, index) => (
            <span key={index}>
                {line}
                <br />
            </span>
        ))}
    </>
);

/** Error modal shown when Salesforce submit fails. */
const DynamicFormErrorModal: React.FC<DynamicFormErrorModalProps> = ({ isOpen, submitError, onClose }) => (
    <Modal
        headline={translate("Error Message")}
        contentLabel={submitError}
        {...styles.successModal}
        isOpen={isOpen}
        handleClose={onClose}
    >
        <GridContainer {...styles.modalContainer}>
            <GridItem {...styles.messageErrorTextGridItem}>
                <Typography as="p" {...styles.messageErrorText}>
                    <DynamicFormErrorLines message={submitError} />
                </Typography>
            </GridItem>
            <GridItem {...styles.buttonGridItem}>
                <Button {...styles.continueButton} onClick={onClose}>
                    {translate("Continue")}
                </Button>
            </GridItem>
        </GridContainer>
    </Modal>
);

type DynamicFormFieldsProps = {
    id: string;
    recaptchaEnabled: boolean;
    recaptchaRef: RefObject<ReCAPTCHA | null>;
    submitError: string;
    isErrorModalOpen: boolean;
    isSuccessModalOpen: boolean;
    successMessage: string;
    submitting: boolean;
    showCancelButton: boolean;
    cancelButtonText: string;
    submitButtonText: string;
    onSubmit: (event: React.FormEvent) => void;
    onCancel: () => void;
    onCloseSuccessModal: () => void;
    onCloseErrorModal: () => void;
};

/** Form markup: field zone, reCAPTCHA, actions, and result modals. */
const DynamicFormFields: React.FC<DynamicFormFieldsProps> = ({
    id,
    recaptchaEnabled,
    recaptchaRef,
    submitError,
    isErrorModalOpen,
    isSuccessModalOpen,
    successMessage,
    submitting,
    showCancelButton,
    cancelButtonText,
    submitButtonText,
    onSubmit,
    onCancel,
    onCloseSuccessModal,
    onCloseErrorModal,
}) => (
    <form id="dynamicForm" onSubmit={onSubmit} noValidate>
        <StyledWrapper {...styles.zoneWrapper}>
            <Zone contentId={id} zoneName="Content" />
        </StyledWrapper>
        {recaptchaEnabled ? (
            <StyledWrapper {...styles.reCaptchaWrapper}>
                <ReCaptcha recaptchaRef={recaptchaRef} location={recaptchaLocation} />
            </StyledWrapper>
        ) : null}
        {submitError && !isErrorModalOpen ? (
            <Typography as="p" color="danger" {...styles.submitError}>
                {submitError}
            </Typography>
        ) : null}
        <DynamicFormActions
            submitting={submitting}
            showCancelButton={showCancelButton}
            cancelButtonText={cancelButtonText}
            submitButtonText={submitButtonText}
            onCancel={onCancel}
        />
        <DynamicFormSuccessModal
            isOpen={isSuccessModalOpen}
            successMessage={successMessage}
            onClose={onCloseSuccessModal}
        />
        <DynamicFormErrorModal
            isOpen={isErrorModalOpen}
            submitError={submitError}
            onClose={onCloseErrorModal}
        />
    </form>
);

/** CMS container widget for Salesforce Dynamic Forms. */
const DynamicForm: React.FC<Props> = ({
    id,
    fields: widgetFields,
    fieldValues,
    isAuthenticated,
    billTo,
    clearDynamicForm,
    validateReCaptcha: widgetValidateReCaptcha,
    resetReCaptcha: widgetResetReCaptcha,
}) => {
    const formId = widgetFields.formId || "Web_DynamicForm";
    const showCancelButton = !!widgetFields.showCancelButton;
    const recaptchaEnabled = !!widgetFields.enableRecaptcha;
    const utmEnabled = !!widgetFields.enableUtm;
    const ga4Enabled = !!widgetFields.enableGa4;
    const ga4EventName = (widgetFields.ga4EventName || "formSubmissionSuccess").trim();
    const ga4FormTypeOverride = (widgetFields.ga4FormType || "").trim();
    const ga4FormType = ga4FormTypeOverride || formId;
    const ga4FormId = (widgetFields.ga4FormId || "dynamicForm").trim();
    const utmSourceMediumField = widgetFields.utmSourceMediumField || "Session_Source_Medium__c";
    const utmCampaignField = widgetFields.utmCampaignField || "Session_Campaign__c";
    const utmChannelGroupField = widgetFields.utmChannelGroupField || "Session_Channel_Group__c";
    const [validators] = useState<{ [key: string]: Validator | undefined }>({});
    const fieldKeysByWidgetId = useRef<{ [widgetId: string]: string[] }>({});
    const attachmentSlotsByWidgetId = useRef<{ [widgetId: string]: DynamicFormAttachmentSlot[] }>({});
    const recaptchaRef: RefObject<ReCAPTCHA | null> = createRef();
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState("");
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);

    // Snapshot landing UTMs into Dynamic Form–only storage (per formId). Does not clear Contact Us / RFQ store.
    useEffect(() => {
        if (utmEnabled && formId) {
            ensureDynamicFormUtmSnapshot(formId);
        }
    }, [utmEnabled, formId]);

    const registerFieldKeys = useCallback((widgetId: string, fieldKeys: string[]) => {
        fieldKeysByWidgetId.current[widgetId] = fieldKeys;
    }, []);

    const registerAttachmentSlots = useCallback((widgetId: string, slots: DynamicFormAttachmentSlot[]) => {
        attachmentSlotsByWidgetId.current[widgetId] = slots;
    }, []);

    const unregisterFieldKeys = useCallback((widgetId: string) => {
        fieldKeysByWidgetId.current = Object.fromEntries(
            Object.entries(fieldKeysByWidgetId.current).filter(([entryKey]) => entryKey !== widgetId),
        );
    }, []);

    const unregisterAttachmentSlots = useCallback((widgetId: string) => {
        attachmentSlotsByWidgetId.current = Object.fromEntries(
            Object.entries(attachmentSlotsByWidgetId.current).filter(([entryKey]) => entryKey !== widgetId),
        );
    }, []);

    const findDuplicateFieldKeys = () => {
        const seen = new Map<string, string>();
        const duplicates = new Set<string>();

        Object.values(fieldKeysByWidgetId.current).forEach(keys => {
            keys.forEach(key => {
                const normalized = key.toLowerCase();
                if (seen.has(normalized)) {
                    duplicates.add(seen.get(normalized) || key);
                } else {
                    seen.set(normalized, key);
                }
            });
        });

        return Array.from(duplicates);
    };

    const handleCancel = () => {
        clearDynamicForm();
        setSubmitError("");
        setIsSuccessModalOpen(false);
        setIsErrorModalOpen(false);
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setSubmitError("");
        setIsSuccessModalOpen(false);
        setIsErrorModalOpen(false);

        const duplicateKeys = findDuplicateFieldKeys();
        if (duplicateKeys.length > 0) {
            setSubmitError(
                translate(
                    "Duplicate Field Keys are not allowed: {0}. Each field widget must use a unique Field Key.",
                    duplicateKeys.join(", "),
                ),
            );
            return;
        }

        const { isValid } = validateFormWithScroll(validators, "dynamicForm", {
            offset: 120,
            behavior: "smooth",
        });

        let isReCaptchaValid = true;
        if (recaptchaEnabled) {
            isReCaptchaValid = await widgetValidateReCaptcha({
                location: recaptchaLocation,
                recaptchaRef: recaptchaRef as RefObject<ReCAPTCHA>,
            });
        }

        if (!isValid) {
            return;
        }

        if (!isReCaptchaValid) {
            scrollToRecaptcha(recaptchaRef);
            return;
        }

        setSubmitting(true);
        try {
            const { fields: fieldsPayload, attachments } = buildDynamicFormSubmitPayload(
                fieldValues,
                attachmentSlotsByWidgetId.current,
            );

            const recaptchaToken = recaptchaEnabled
                ? recaptchaRef.current?.getValue() || getCookie("g-recaptcha-response") || ""
                : "";

            if (utmEnabled) {
                applyDynamicFormUtmToFields(fieldsPayload, getDynamicFormUtm(formId), {
                    sourceMediumField: utmSourceMediumField,
                    campaignField: utmCampaignField,
                    channelGroupField: utmChannelGroupField,
                });
            }

            const result = await submitDynamicForm(formId, {
                fields: fieldsPayload,
                attachments: attachments.length ? attachments : undefined,
                recaptchaEnabled,
                recaptchaToken,
            });
            if (!result.successful) {
                setSubmitError(result.errorMessage || translate("Submit failed."));
                setIsErrorModalOpen(true);
                return;
            }

            if (ga4Enabled) {
                const sapAccountNumber =
                    isAuthenticated && billTo?.customerNumber
                        ? billTo.customerNumber.replace(/^0+/, "")
                        : "";
                setGA4Event([], ga4EventName, {
                    formType: ga4FormType,
                    formID: ga4FormId,
                    accountNumber: sapAccountNumber,
                });
            }

            setIsSuccessModalOpen(true);
            clearDynamicForm();
            if (utmEnabled) {
                clearDynamicFormUtm(formId);
            }
            sessionStorage.setItem("clearUploadFiles", "true");
            if (recaptchaEnabled) {
                widgetResetReCaptcha({ location: recaptchaLocation });
            }
        } finally {
            setSubmitting(false);
        }
    };

    const closeSuccessModal = () => {
        setIsSuccessModalOpen(false);
    };

    const closeErrorModal = () => {
        setIsErrorModalOpen(false);
    };

    const successMessage =
        widgetFields.successMessage ||
        translate("Thank you for your inquiry. A customer service representative will contact you shortly.");

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.formGridItem}>
                <DynamicFormContext.Provider
                    value={{
                        validators,
                        registerFieldKeys,
                        registerAttachmentSlots,
                        unregisterFieldKeys,
                        unregisterAttachmentSlots,
                    }}
                >
                    <DynamicFormFields
                        id={id}
                        recaptchaEnabled={recaptchaEnabled}
                        recaptchaRef={recaptchaRef}
                        submitError={submitError}
                        isErrorModalOpen={isErrorModalOpen}
                        isSuccessModalOpen={isSuccessModalOpen}
                        successMessage={successMessage}
                        submitting={submitting}
                        showCancelButton={showCancelButton}
                        cancelButtonText={widgetFields.cancelButtonText || translate("Cancel")}
                        submitButtonText={
                            submitting
                                ? translate("Submitting...")
                                : widgetFields.submitButtonText || translate("Submit")
                        }
                        onSubmit={handleSubmit}
                        onCancel={handleCancel}
                        onCloseSuccessModal={closeSuccessModal}
                        onCloseErrorModal={closeErrorModal}
                    />
                </DynamicFormContext.Provider>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(DynamicForm),
    definition: {
        group: widgetGroup,
        displayName: "Dynamic Form",
        icon: "clipboard-list",
        fieldDefinitions: [
            {
                name: fields.formId,
                displayName: "Form Id (LeadSource)",
                editorTemplate: "TextField",
                defaultValue: "Web_DynamicForm",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.submitButtonText,
                displayName: "Submit Button Text",
                editorTemplate: "TextField",
                defaultValue: "Submit",
                fieldType: "Translatable",
            },
            {
                name: fields.showCancelButton,
                displayName: "Show Cancel Button",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tooltip: "When enabled, the Cancel button is shown on the form and Cancel Button Text can be edited.",
                dependentFields: [fields.cancelButtonText],
            },
            {
                name: fields.cancelButtonText,
                displayName: "Cancel Button Text",
                editorTemplate: "TextField",
                defaultValue: "Cancel",
                fieldType: "Translatable",
                isVisible: (item: HasFields) => !!item.fields[fields.showCancelButton],
            },
            {
                name: fields.successMessage,
                displayName: "Submit Success Message",
                editorTemplate: "RichTextField",
                defaultValue: "Thank you for your inquiry. A customer service representative will contact you shortly.",
                fieldType: "Translatable",
            },
            {
                name: fields.enableRecaptcha,
                displayName: "Enable reCAPTCHA",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.enableUtm,
                displayName: "Send UTM parameters",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tooltip:
                    "When enabled, landing-page utm_source / utm_medium / utm_campaign are mapped into the Salesforce fields below.",
                dependentFields: [
                    fields.utmSourceMediumField,
                    fields.utmCampaignField,
                    fields.utmChannelGroupField,
                ],
            },
            {
                name: fields.utmSourceMediumField,
                displayName: "UTM Source/Medium Salesforce Field",
                editorTemplate: "TextField",
                defaultValue: "Session_Source_Medium__c",
                fieldType: "General",
                isRequired: true,
                isVisible: (item: HasFields) => !!item.fields[fields.enableUtm],
                tooltip: "Salesforce API name that receives combined utm_source / utm_medium.",
            },
            {
                name: fields.utmCampaignField,
                displayName: "UTM Campaign Salesforce Field",
                editorTemplate: "TextField",
                defaultValue: "Session_Campaign__c",
                fieldType: "General",
                isRequired: true,
                isVisible: (item: HasFields) => !!item.fields[fields.enableUtm],
                tooltip: "Salesforce API name that receives utm_campaign.",
            },
            {
                name: fields.utmChannelGroupField,
                displayName: "UTM Channel Group Salesforce Field",
                editorTemplate: "TextField",
                defaultValue: "Session_Channel_Group__c",
                fieldType: "General",
                isRequired: true,
                isVisible: (item: HasFields) => !!item.fields[fields.enableUtm],
                tooltip: "Salesforce API name that receives the derived channel group from utm_medium.",
            },
            {
                name: fields.enableGa4,
                displayName: "Send GA4 events",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tooltip:
                    "When enabled, a GA4/GTM dataLayer event is sent after a successful Salesforce submit.",
                dependentFields: [fields.ga4EventName, fields.ga4FormType, fields.ga4FormId],
            },
            {
                name: fields.ga4EventName,
                displayName: "GA4 Event Name",
                editorTemplate: "TextField",
                defaultValue: "formSubmissionSuccess",
                fieldType: "General",
                isRequired: true,
                isVisible: (item: HasFields) => !!item.fields[fields.enableGa4],
                tooltip: "dataLayer event name pushed to GTM (default matches Contact Us and RFQ forms).",
            },
            {
                name: fields.ga4FormType,
                displayName: "GA4 Form Type",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                isVisible: (item: HasFields) => !!item.fields[fields.enableGa4],
                tooltip:
                    "Optional marketing label sent as formType. When empty, Form Id (LeadSource) is used.",
            },
            {
                name: fields.ga4FormId,
                displayName: "GA4 Form ID",
                editorTemplate: "TextField",
                defaultValue: "dynamicForm",
                fieldType: "General",
                isRequired: true,
                isVisible: (item: HasFields) => !!item.fields[fields.enableGa4],
                tooltip: "Identifier sent as formID in the dataLayer event (separate from Form Id / LeadSource).",
            },
        ],
    },
};

export default widgetModule;
